import React from 'react';
import { useCourseAccess } from '../hooks/useCourseAccess';

interface CourseAccessGuardProps {
  children: React.ReactNode;
  requiredAccess?: 'Uni' | 'First' | 'Manager';
  requiredModule?: number;
  fallback?: React.ReactNode;
}

export const CourseAccessGuard: React.FC<CourseAccessGuardProps> = ({
  children,
  requiredAccess,
  requiredModule,
  fallback
}) => {
  const { 
    courseAccess, 
    isUni, 
    isFirst, 
    isManager, 
    hasModuleAccess, 
    getAccessLevelName,
    getAccessLevelDescription 
  } = useCourseAccess();

  const hasAccess = () => {
    // If module is specified, check module access
    if (requiredModule) {
      return hasModuleAccess(requiredModule);
    }
    
    // If access level is specified, check access level
    if (requiredAccess) {
      switch (requiredAccess) {
        case 'Manager':
          return isManager;
        case 'First':
          return isFirst || isManager;
        case 'Uni':
          return isUni || isFirst || isManager;
        default:
          return false;
      }
    }
    
    // Default to true if no requirements specified
    return true;
  };

  if (!hasAccess()) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Access Restricted
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                {requiredModule 
                  ? `This content requires access to Module ${requiredModule}. Your current access level is: ${getAccessLevelName()}.`
                  : `This content requires ${requiredAccess} access. Your current access level is: ${getAccessLevelName()}.`
                }
              </p>
              <p className="mt-1">
                {getAccessLevelDescription()}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}; 